import { useEffect, useState } from 'react';
import { FileText, Download, CheckCircle, Clock, AlertCircle, Filter, Loader2 } from 'lucide-react';

interface Invoice {
  id: string;
  status: 'draft' | 'confirmed';
  total_declared_usd: number;
  total_items: number;
  confirmed_at?: string;
  created_at: string;
  shopping_list: {
    id: string;
    total_usd: number;
    total_brl: number;
    status: string;
    user: {
      id: string;
      name: string;
      email: string;
    };
  };
}

export default function Invoices() {
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState<'all' | 'draft' | 'confirmed'>('all');
  const [downloading, setDownloading] = useState<string | null>(null);

  useEffect(() => {
    loadInvoices();
  }, [filterStatus]);

  const loadInvoices = async () => {
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';
      const token = localStorage.getItem('token');
      
      const url = filterStatus === 'all' 
        ? `${apiUrl}/api/invoices/list`
        : `${apiUrl}/api/invoices/list?status=${filterStatus}`;

      const res = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await res.json();

      if (data.success) {
        setInvoices(data.data);
      } else {
        alert(data.error || 'Erro ao carregar invoices');
      }
    } catch (error) {
      console.error('Erro ao carregar invoices:', error);
      alert('Erro ao carregar invoices');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPDF = async (invoiceId: string) => {
    try {
      setDownloading(invoiceId);
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';
      const token = localStorage.getItem('token');

      const res = await fetch(`${apiUrl}/api/invoices/${invoiceId}/pdf`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Erro ao gerar PDF');
      }

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `invoice-${invoiceId.substring(0, 8)}.html`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      alert('✅ Invoice baixada com sucesso!');
    } catch (error: any) {
      console.error('Erro ao baixar PDF:', error);
      alert(error.message || 'Erro ao baixar PDF');
    } finally {
      setDownloading(null);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'confirmed':
        return (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
            <CheckCircle className="h-4 w-4 mr-1" />
            Confirmada
          </span>
        );
      case 'draft':
        return (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
            <Clock className="h-4 w-4 mr-1" />
            Rascunho
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
            <AlertCircle className="h-4 w-4 mr-1" />
            {status}
          </span>
        );
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 flex items-center">
          <FileText className="h-8 w-8 mr-3 text-blue-600" />
          Declarações Aduaneiras (Invoices)
        </h1>
        <p className="text-gray-600 mt-2">
          Consulte e baixe as declarações aduaneiras confirmadas
        </p>
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="flex items-center gap-4">
          <Filter className="h-5 w-5 text-gray-500" />
          <span className="text-sm font-medium text-gray-700">Filtrar por status:</span>
          <div className="flex gap-2">
            <button
              onClick={() => setFilterStatus('all')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                filterStatus === 'all'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Todas
            </button>
            <button
              onClick={() => setFilterStatus('confirmed')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                filterStatus === 'confirmed'
                  ? 'bg-green-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Confirmadas
            </button>
            <button
              onClick={() => setFilterStatus('draft')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                filterStatus === 'draft'
                  ? 'bg-yellow-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Rascunhos
            </button>
          </div>
        </div>
      </div>

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
          <span className="ml-3 text-gray-600">Carregando invoices...</span>
        </div>
      )}

      {/* Lista de Invoices */}
      {!loading && invoices.length === 0 && (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <FileText className="h-16 w-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Nenhuma invoice encontrada</h3>
          <p className="text-gray-600">
            {filterStatus === 'all'
              ? 'Não há declarações aduaneiras no sistema ainda.'
              : `Não há invoices com status "${filterStatus}".`}
          </p>
        </div>
      )}

      {!loading && invoices.length > 0 && (
        <div className="grid grid-cols-1 gap-6">
          {invoices.map((invoice) => (
            <div key={invoice.id} className="bg-white rounded-lg shadow hover:shadow-lg transition-shadow">
              <div className="p-6">
                {/* Header */}
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      Invoice #{invoice.id.substring(0, 8)}
                    </h3>
                    <p className="text-sm text-gray-600 mt-1">
                      Cliente: <span className="font-medium">{invoice.shopping_list.user.name}</span>
                    </p>
                    <p className="text-xs text-gray-500">
                      {invoice.shopping_list.user.email}
                    </p>
                  </div>
                  <div className="text-right">
                    {getStatusBadge(invoice.status)}
                    <p className="text-xs text-gray-500 mt-2">
                      Criada em: {new Date(invoice.created_at).toLocaleDateString('pt-BR')}
                    </p>
                  </div>
                </div>

                {/* Dados */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 py-4 border-t border-b border-gray-200">
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Total Declarado</p>
                    <p className="text-lg font-bold text-blue-600">
                      USD {invoice.total_declared_usd.toFixed(2)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Itens</p>
                    <p className="text-lg font-semibold text-gray-900">
                      {invoice.total_items}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Valor Total (BRL)</p>
                    <p className="text-lg font-semibold text-gray-900">
                      R$ {invoice.shopping_list.total_brl.toFixed(2)}
                    </p>
                  </div>
                </div>

                {/* Confirmação */}
                {invoice.status === 'confirmed' && invoice.confirmed_at && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3 mt-4">
                    <p className="text-sm text-green-800">
                      ✅ Confirmada em: {new Date(invoice.confirmed_at).toLocaleString('pt-BR')}
                    </p>
                  </div>
                )}

                {/* Ações */}
                <div className="flex gap-3 mt-4">
                  {invoice.status === 'confirmed' && (
                    <button
                      onClick={() => handleDownloadPDF(invoice.id)}
                      disabled={downloading === invoice.id}
                      className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-lg font-medium hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center transition"
                    >
                      {downloading === invoice.id ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Gerando...
                        </>
                      ) : (
                        <>
                          <Download className="h-4 w-4 mr-2" />
                          Baixar Invoice (HTML)
                        </>
                      )}
                    </button>
                  )}

                  {invoice.status === 'draft' && (
                    <div className="flex-1 bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                      <p className="text-sm text-yellow-800">
                        ⚠️ Esta invoice ainda não foi confirmada. Complete o checkout para confirmar.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Info adicional */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-8">
        <h4 className="font-medium text-blue-900 mb-2">ℹ️ Sobre as Invoices</h4>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>• As invoices confirmadas são declarações oficiais para a Receita Federal</li>
          <li>• Você pode baixar o PDF das invoices confirmadas</li>
          <li>• Invoices em rascunho ainda podem ser editadas durante o checkout</li>
          <li>• Após confirmação, os valores não podem mais ser alterados</li>
        </ul>
      </div>
    </div>
  );
}
